import { useTranslation } from 'react-i18next'

import { Button } from 'primereact/button'
import { Checkbox } from 'primereact/checkbox'
import { Dialog } from 'primereact/dialog'
import { Divider } from 'primereact/divider'
import { Dropdown } from 'primereact/dropdown'
import { InputNumber } from 'primereact/inputnumber'
import { InputText } from 'primereact/inputtext'

import { calculateBreakpointWidth } from '../../../../../../../utils/helper/calculateBreakpointWidth'
import { ConfigurableComponent } from '../../../../../../../utils/wrapper'
import { useConfigContext } from '../../../../../../ui-config'
import { usePackagingMaterialById } from '../../../data'

interface FormProps {
  id?: string
  isVisible: boolean
  onHide: () => void
}

export const InfoPackagingData: React.FC<FormProps> = ({
  id,
  isVisible,
  onHide,
}) => {
  const { t, i18n } = useTranslation()
  const { language } = i18n

  const { uiConfig: config } = useConfigContext()
  const { data: packaging } = usePackagingMaterialById(id)

  // PackagingDataId
  const packagingDataIdLabel = (
    <label htmlFor="packagingDataId" className="text-sm"></label>
  )

  const packagingDataIdComponent = (
    <InputText
      id="packagingDataId"
      className="w-full"
      value={packaging?.PackagingDataId}
      disabled
    />
  )

  const packagingDataIdWrapper = (
    <ConfigurableComponent
      config={config.selfservices.masterDataManagement.packagingData.info}
      component={packagingDataIdComponent}
      label={packagingDataIdLabel}
    />
  )

  // description
  const descriptionLabel = (
    <label htmlFor="description" className="text-sm"></label>
  )

  const descriptionComponent = (
    <InputText
      id="description"
      className="w-full"
      value={packaging?.Description}
      disabled
    />
  )

  const descriptionWrapper = (
    <ConfigurableComponent
      config={config.selfservices.masterDataManagement.packagingData.info}
      component={descriptionComponent}
      label={descriptionLabel}
    />
  )

  // PackagingMaterial
  const packagingMaterialLabel = (
    <label htmlFor="packagingMaterial" className="text-sm"></label>
  )

  const packagingMaterialComponent = (
    <Dropdown
      id="packagingMaterial"
      className="w-full"
      value={packaging?.PackagingMaterial}
      optionLabel="code"
      optionValue="code"
      disabled
    />
  )

  const packagingMaterialWrapper = (
    <ConfigurableComponent
      config={config.selfservices.masterDataManagement.packagingData.info}
      component={packagingMaterialComponent}
      label={packagingMaterialLabel}
    />
  )

  // length
  const lengthLabel = <label htmlFor="length" className="text-sm"></label>

  const lengthComponent = (
    <InputNumber
      id="length"
      className="w-full"
      value={packaging?.Measurements?.Length}
      locale={language}
      minFractionDigits={2}
      disabled
    />
  )

  const lengthWrapper = (
    <ConfigurableComponent
      config={config.selfservices.masterDataManagement.packagingData.info}
      component={lengthComponent}
      label={lengthLabel}
    />
  )

  // width
  const widthLabel = <label htmlFor="width" className="text-sm"></label>

  const widthComponent = (
    <InputNumber
      id="width"
      className="w-full"
      value={packaging?.Measurements?.Width}
      locale={language}
      minFractionDigits={2}
      disabled
    />
  )

  const widthWrapper = (
    <ConfigurableComponent
      config={config.selfservices.masterDataManagement.packagingData.info}
      component={widthComponent}
      label={widthLabel}
    />
  )

  // height
  const heightLabel = <label htmlFor="height" className="text-sm"></label>

  const heightComponent = (
    <InputNumber
      id="height"
      className="w-full"
      value={packaging?.Measurements?.Height}
      locale={language}
      minFractionDigits={2}
      disabled
    />
  )

  const heightWrapper = (
    <ConfigurableComponent
      config={config.selfservices.masterDataManagement.packagingData.info}
      component={heightComponent}
      label={heightLabel}
    />
  )

  // MeasurementsUnit
  const measurementsUnitLabel = (
    <label htmlFor="measurementsUnit" className="text-sm"></label>
  )

  const measurementsUnitComponent = (
    <Dropdown
      id="measurementsUnit"
      className="w-full"
      value={packaging?.Measurements?.Unit}
      optionLabel="code"
      optionValue="code"
      disabled
    />
  )

  const measurementsUnitWrapper = (
    <ConfigurableComponent
      config={config.selfservices.masterDataManagement.packagingData.info}
      component={measurementsUnitComponent}
      label={measurementsUnitLabel}
    />
  )

  // tareWeight
  const tareWeightLabel = (
    <label htmlFor="tareWeight" className="text-sm"></label>
  )

  const tareWeightComponent = (
    <InputNumber
      id="tareWeight"
      className="w-full"
      value={packaging?.TareWeight?.Value}
      locale={language}
      minFractionDigits={3}
      disabled
    />
  )

  const tareWeightWrapper = (
    <ConfigurableComponent
      config={config.selfservices.masterDataManagement.packagingData.info}
      component={tareWeightComponent}
      label={tareWeightLabel}
    />
  )

  // tareWeightUnit
  const tareWeightUnitLabel = (
    <label htmlFor="tareWeightUnit" className="text-sm"></label>
  )

  const tareWeightUnitComponent = (
    <Dropdown
      id="tareWeightUnit"
      className="w-full"
      value={packaging?.TareWeight?.Unit}
      optionLabel="code"
      optionValue="code"
      disabled
    />
  )

  const tareWeightUnitWrapper = (
    <ConfigurableComponent
      config={config.selfservices.masterDataManagement.packagingData.info}
      component={tareWeightUnitComponent}
      label={tareWeightUnitLabel}
    />
  )

  // Volume
  const volumeLabel = <label htmlFor="volume" className="text-sm"></label>

  const volumeComponent = (
    <InputNumber
      id="volume"
      className="w-full"
      value={packaging?.Volume?.Value}
      locale={language}
      minFractionDigits={3}
      disabled
    />
  )

  const volumeWrapper = (
    <ConfigurableComponent
      config={config.selfservices.masterDataManagement.packagingData.info}
      component={volumeComponent}
      label={volumeLabel}
    />
  )

  // VolumeUnit
  const volumeUnitLabel = (
    <label htmlFor="volumeUnit" className="text-sm"></label>
  )

  const volumeUnitComponent = (
    <Dropdown
      id="volumeUnit"
      className="w-full"
      value={packaging?.Volume?.Unit}
      optionLabel="code"
      optionValue="code"
      disabled
    />
  )

  const volumeUnitWrapper = (
    <ConfigurableComponent
      config={config.selfservices.masterDataManagement.packagingData.info}
      component={volumeUnitComponent}
      label={volumeUnitLabel}
    />
  )

  // packagingIdentifier
  const packagingIdentifierLabel = (
    <label htmlFor="packagingIdentifier" className="text-sm"></label>
  )

  const packagingIdentifierComponent = (
    <Dropdown
      id="packagingIdentifier"
      className="w-full"
      value={packaging?.PackagingIdentifier}
      optionLabel="code"
      optionValue="code"
      disabled
    />
  )

  const packagingIdentifierWrapper = (
    <ConfigurableComponent
      config={config.selfservices.masterDataManagement.packagingData.info}
      component={packagingIdentifierComponent}
      label={packagingIdentifierLabel}
    />
  )

  // propertyIdentifier
  const propertyIdentifierLabel = (
    <label htmlFor="propertyIdentifier" className="text-sm"></label>
  )

  const propertyIdentifierComponent = (
    <Dropdown
      id="propertyIdentifier"
      className="w-full"
      value={packaging?.PropertyIdentifier}
      optionLabel="code"
      optionValue="code"
      disabled
    />
  )

  const propertyIdentifierWrapper = (
    <ConfigurableComponent
      config={config.selfservices.masterDataManagement.packagingData.info}
      component={propertyIdentifierComponent}
      label={propertyIdentifierLabel}
    />
  )

  // IsStackable
  const isStackableLabel = (
    <label htmlFor="isStackable" className="text-sm"></label>
  )

  const isStackableComponent = (
    <div
      className="flex align-packagings-center"
      id="isStackable"
      data-checkbox
    >
      <Checkbox
        id="isStackable"
        inputId="isStackable"
        name="isStackable"
        value="yes"
        checked={packaging?.IsStackable || false}
        disabled
      />
      <label htmlFor="isStackable" className="ml-2">
        {t(
          'masterDataManagement.packagingData.table.info.dialog.form.isStackable.checkbox.label',
        )}
      </label>
    </div>
  )

  const isStackableWrapper = (
    <ConfigurableComponent
      config={config.selfservices.masterDataManagement.packagingData.info}
      component={isStackableComponent}
      label={isStackableLabel}
    />
  )

  // Footer
  const footer = (
    <Button
      label={t(
        'masterDataManagement.packagingData.table.info.dialog.close.label',
      )}
      icon={'pi pi-check'}
      className="w-full md:w-auto"
      onClick={onHide}
    />
  )

  return (
    <Dialog
      header={t('masterDataManagement.packagingData.table.info.dialog.title')}
      visible={isVisible}
      style={{ width: '55rem' }}
      breakpoints={{ [calculateBreakpointWidth(55)]: '90vw' }}
      footer={footer}
      onHide={onHide}
      blockScroll
    >
      <div className="formgrid grid">
        <div className="col-12 md:col-6">{packagingDataIdWrapper}</div>
        <div className="col-12 md:col-6">{descriptionWrapper}</div>
        <div className="col-12 md:col-6">{packagingMaterialWrapper}</div>
      </div>

      <Divider className="md:hidden" />

      <div className="formgrid grid">
        {/* length */}
        <div className="col-12 md:col-3">{lengthWrapper}</div>
        {/* width */}
        <div className="col-12 md:col-3">{widthWrapper}</div>
        {/* height */}
        <div className="col-12 md:col-3">{heightWrapper}</div>
        {/* dimensionUnit */}
        <div className="col-12 md:col-3">{measurementsUnitWrapper}</div>
      </div>

      <Divider className="md:hidden" />

      <div className="formgrid grid">
        {/* tareWeight */}
        <div className="col-12 md:col-6">{tareWeightWrapper}</div>
        {/* tareWeightUnit */}
        <div className="col-12 md:col-6">{tareWeightUnitWrapper}</div>
      </div>

      <Divider className="md:hidden" />

      <div className="formgrid grid">
        {/* volume */}
        <div className="col-12 md:col-6">{volumeWrapper}</div>
        {/* volumeUnit */}
        <div className="col-12 md:col-6">{volumeUnitWrapper}</div>
      </div>

      <Divider className="md:hidden" />

      <div className="formgrid grid">
        <div className="col-12 md:col-6">{packagingIdentifierWrapper}</div>

        {packaging?.PackagingIdentifier === 'reusablePackaging' && (
          <div className="col-12 md:col-6">{propertyIdentifierWrapper}</div>
        )}
      </div>

      <Divider className="md:hidden" />

      <div className="formgrid grid">
        <div className="col-12 md:col-6">{isStackableWrapper}</div>
      </div>
    </Dialog>
  )
}
